<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { nextTick } from 'vue'; // Import nextTick for notification
import { API_BASE_URL } from '@/config';

// Reactive state for the list of failure codes
const failureCodes = ref([]);
// Reactive state for the new failure code form input
const newFailureCode = ref({ failure_code: '', failure_desc: '' });
// Reactive state to hold the failure code currently being edited (null if not editing)
const editingFailureCode = ref(null);

// Reactive state to track the row clicked in the table for Edit/Delete actions
const selectedFailureCode = ref(null);


// !! IMPORTANT: Replace with the actual base URL of your Node.js backend !!
// This URL should match where your backend is running and where the /api/failurecodes routes are mounted.
const API_failureCodes_URL = '${API_BASE_URL}/api/failurecodes'; // Corrected API endpoint


const loading = ref(false); // Loading state for async operations (e.g., fetching, saving, deleting)
const error = ref(null); // Error state for displaying persistent error messages
// State for managing temporary notification messages (success, error, warning)
const notification = ref({
  show: false,
  message: '',
  type: '' // 'success', 'error', 'warning'
});

// --- Computed Properties for v-model Binding ---
// This computed property handles the binding for the Failure Code input field.
// It switches between the 'editingFailureCode' and 'newFailureCode' objects based on whether editing is active.
const currentFailureCode = computed({
  get() {
    // If editing, get the failure_code from the editing object, otherwise from the new object
    return editingFailureCode.value ? editingFailureCode.value.failure_code : newFailureCode.value.failure_code;
  },
  set(value) {
    // If editing, update the failure_code in the editing object, otherwise in the new object
    if (editingFailureCode.value) {
      editingFailureCode.value.failure_code = value;
    } else {
      newFailureCode.value.failure_code = value;
    }
  }
});

// This computed property handles the binding for the Failure Description input field.
// It switches between the 'editingFailureCode' and 'newFailureCode' objects based on whether editing is active.
const currentFailureDesc = computed({
  get() {
     // If editing, get the failure_desc from the editing object, otherwise from the new object
    return editingFailureCode.value ? editingFailureCode.value.failure_desc : newFailureCode.value.failure_desc;
  },
  set(value) {
    if (editingFailureCode.value) {
      editingFailureCode.value.failure_desc = value;
    } else {
      newFailureCode.value.failure_desc = value;
    }
  }
});

// --- End Computed Properties ---


// --- CRUD Operations ---

// Fetches all failure codes from the backend API
const fetchFailureCodes = async () => {
  loading.value = true; // Set loading state to true
  error.value = null; // Clear any previous errors
  selectedFailureCode.value = null; // Deselect any selected item when refreshing the list
  try {
    // Send GET request to the API endpoint
    const response = await axios.get(API_failureCodes_URL);
    // Update the failureCodes ref with the data received from the backend
    failureCodes.value = response.data;
  } catch (err) {
    console.error('Error fetching failure codes:', err);
    error.value = 'Failed to fetch failure codes. Please try again later.';
    showNotification(error.value, 'error'); // Show an error notification
  } finally {
    loading.value = false; // Set loading state to false regardless of success or failure
  }
};

// Creates a new failure code by sending data to the backend API
const createFailureCode = async () => {
  // Basic client-side validation: check if required fields are not empty
  if (!newFailureCode.value.failure_code.trim() || !newFailureCode.value.failure_desc.trim()) {
    showNotification('Failure Code and Description are required.', 'warning');
    return; // Stop the function if validation fails
  }
  loading.value = true; // Set loading state
  error.value = null; // Clear previous errors
  try {
    // Send POST request to the API endpoint with the new failure code data
    const response = await axios.post(API_failureCodes_URL, newFailureCode.value);

    // --- DEBUGGING LOG ---
    console.log('Frontend: Backend response data for new failure code:', response.data);
    // --- END DEBUGGING LOG ---

    // Add the newly created item (returned from the backend) to the local list
    // This should trigger Vue to update the table display IF response.data is valid
    failureCodes.value.push(response.data);
    // Clear the form inputs
    newFailureCode.value = { failure_code: '', failure_desc: '' };
    showNotification('Failure code created successfully!', 'success'); // Show success notification
  } catch (err) {
    console.error('Error creating failure code:', err);
    // Handle specific error responses from the backend
    if (err.response?.status === 409) {
      error.value = 'Failure Code already exists.';
    } else {
      // This will catch potential 500 errors from the backend
      error.value = err.response?.data || 'Failed to create failure code.';
    }
    showNotification(error.value, 'error'); // Show error notification
  } finally {
    loading.value = false; // Set loading state to false
  }
};

// Updates an existing failure code by sending data to the backend API
const updateFailureCode = async () => {
   // Ensure we are in editing mode and a failure code is selected
   if (!editingFailureCode.value || !selectedFailureCode.value) {
       showNotification('No failure code selected for editing.', 'warning');
       return;
   }
   // Basic client-side validation for the description (the only updateable field)
  if (!editingFailureCode.value.failure_desc.trim()) {
    showNotification('Failure Description is required.', 'warning');
    return;
  }

  loading.value = true; // Set loading state
  error.value = null; // Clear previous errors
  try {
    // Create a payload with only the updateable field(s)
    const payload = { failure_desc: editingFailureCode.value.failure_desc };
    // Send PUT request to the specific item endpoint using the failure_code from the selected item
    // Uses JavaScript template literals (backticks `` and ${}) to build the URL dynamically
    const response = await axios.put(`${API_failureCodes_URL}/${selectedFailureCode.value.failure_code}`, payload); // <-- This line builds the correct URL

    // Find the index of the updated item in the local list using its failure_code
    const index = failureCodes.value.findIndex(item => item.failure_code === selectedFailureCode.value.failure_code); // <-- Use failure_code for finding index
    if (index !== -1) {
      // Update the item in the local list with the data returned from the backend
      failureCodes.value[index] = { ...failureCodes.value[index], ...response.data };
    }
     // If the updated item was the one currently selected, update the selected item ref as well
     if (selectedFailureCode.value && selectedFailureCode.value.failure_code === selectedFailureCode.value.failure_code) { // This condition is always true, could just update directly
         selectedFailureCode.value = { ...selectedFailureCode.value, ...response.data }; // <-- Update selected item
     }

    editingFailureCode.value = null; // Exit editing mode
    showNotification('Failure code updated successfully!', 'success'); // Show success notification
  } catch (err) {
    console.error('Error updating failure code:', err);
    // Handle specific error responses from the backend
    if (err.response?.status === 404) {
      error.value = 'Failure code not found.';
    } else if (err.response?.status === 400) {
            // Handle specific backend validation errors like description missing
            error.value = err.response.data || 'Bad Request: Could not update failure code.';
        }
    else {
      error.value = 'Failed to update failure code.';
    }
    showNotification(error.value, 'error'); // Show error notification
  } finally {
    loading.value = false; // Set loading state to false
  }
};

// Deletes a failure code by sending a DELETE request to the backend API
const deleteFailureCode = async () => {
    // Ensure a failure code is selected for deletion
    if (!selectedFailureCode.value) {
        showNotification('Please select a failure code to delete.', 'warning');
        return;
    }
  // Get the failure_code of the item to delete from the selected item
  const failureCodeToDelete = selectedFailureCode.value.failure_code; // Get the code from the selected item

  // Show a confirmation prompt to the user
  if (confirm(`Are you sure you want to delete failure code ${failureCodeToDelete}? This may also delete associated records.`)) {
    loading.value = true; // Set loading state
    error.value = null; // Clear previous errors
    try {
      // Send DELETE request to the specific item endpoint using the failure_code
       // Uses JavaScript template literals (backticks `` and ${}) to build the URL dynamically
      await axios.delete(`${API_failureCodes_URL}/${failureCodeToDelete}`); // <-- This line builds the correct URL
      // Remove the deleted item from the local list by filtering
      failureCodes.value = failureCodes.value.filter(item => item.failure_code !== failureCodeToDelete);
      // Clear selected and editing states if they match the deleted item
      selectedFailureCode.value = null;
      if(editingFailureCode.value && editingFailureCode.value.failure_code === failureCodeToDelete) {
           editingFailureCode.value = null;
      }
      showNotification('Failure code deleted successfully!', 'success'); // Show success notification
    } catch (err) {
      console.error('Error deleting failure code:', err);
      // Handle specific error responses from the backend
      if (err.response?.status === 404) {
        error.value = 'Failure code not found.';
      } else if (err.response?.status === 409) {
           // Specific error message from the backend about Foreign Key conflicts
           error.value = err.response.data || 'Conflict: Cannot delete failure code because it is referenced by other entities.'; // Use backend message or a default
      }
      else {
        error.value = 'Failed to delete failure code.';
      }
      showNotification(error.value, 'error'); // Show error notification
    } finally {
      loading.value = false; // Set loading state to false
    }
  }
};

// --- UI Actions ---

// Selects a row in the table when clicked
const selectFailureCode = (item) => {
  selectedFailureCode.value = item;
    // If you select an item while currently editing another, cancel the editing
    if (editingFailureCode.value) {
        cancelEditing();
    }
    error.value = null; // Clear previous errors when selecting a new row
}

// Initiates the editing process for the currently selected failure code item
const startEditing = () => {
  if (selectedFailureCode.value) {
       // Copy selected item data to the editing state to populate the form
       editingFailureCode.value = { ...selectedFailureCode.value };
       // Clear the new failure code form when starting edit mode
       newFailureCode.value = { failure_code: '', failure_desc: '' };
       error.value = null; // Clear any previous errors
  } else {
       showNotification('Please select a failure code to edit.', 'warning'); // Prompt user if no item is selected
  }
};

// Cancels the current editing process and clears the editing state
const cancelEditing = () => {
  editingFailureCode.value = null; // Clear the editing state
  // Clear the new failure code form (as it shares inputs via computed properties)
  newFailureCode.value = { failure_code: '', failure_desc: '' };
  error.value = null; // Clear any previous errors
};

// Resets the form to the state for adding a new failure code item
 const addNew = () => {
     editingFailureCode.value = null; // Ensure not in editing mode
     selectedFailureCode.value = null; // Deselect any row in the table
     // Clear form for new entry
     newFailureCode.value = { failure_code: '', failure_desc: '' };
     error.value = null; // Clear any previous errors
 };

// Helper function to show a temporary notification message
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;

   // Use nextTick to ensure the notification element is in the DOM before trying to animate
   nextTick(() => {
        const notificationElement = document.querySelector('.notification');
        if (notificationElement) {
             // Ensure fade-out class is removed before reapplying to allow animation restart
             notificationElement.classList.remove('fade-out');
             // Trigger a reflow to ensure the class removal is registered by the browser
             void notificationElement.offsetWidth;
             // Add fade-out class after a delay to start the fade animation
            setTimeout(() => {
                     notificationElement.classList.add('fade-out');
                     // Hide completely after fade-out transition ends
                     notificationElement.addEventListener('transitionend', () => {
                          // Only hide if the element still has the fade-out class (prevents issues if state changes rapidly)
                          if (!notificationElement.classList.contains('fade-out')) return;
                           notification.value.show = false;
                     }, { once: true }); // Remove the event listener after it triggers once
                }, 5000); // Start fade-out after 5 seconds
            } else {
                 // Fallback to just hiding after a total time if the element wasn't found (shouldn't happen if show is true)
                 setTimeout(() => {
                     notification.value.show = false;
                 }, 5500); // Total time including implied fade duration
            }
       });
    };

// --- Lifecycle Hook ---
// Fetch failure codes from the backend when the component is first mounted to the DOM
onMounted(fetchFailureCodes);
</script>

<template>
  <div class="failure-code-page"> <div class="container">
      <div class="form-panel">
        <h2>{{ editingFailureCode ? 'Edit Failure Code' : 'Add New Failure Code' }}</h2>
        <form @submit.prevent="editingFailureCode ? updateFailureCode() : createFailureCode()">
          <div class="form-group">
            <label for="failure_code">Failure Code:</label>
              <input type="text" id="failure_code" v-model="currentFailureCode" required
                       :disabled="editingFailureCode !== null || loading">
           </div>
          <div class="form-group">
            <label for="failure_desc">Failure Description:</label>
               <input type="text" id="failure_desc" v-model="currentFailureDesc" required
                       :disabled="loading">
           </div>

          <div class="form-actions">
            <button type="submit" class="save-button" :disabled="loading">
              {{ editingFailureCode ? 'Update Code' : 'Save Code' }}
            </button>
            <button type="button" class="cancel-button" @click="cancelEditing" v-if="editingFailureCode" :disabled="loading">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div class="table-panel">
        <h2>Failure Code List</h2>

          <div v-if="loading && failureCodes.length === 0">Loading failure codes...</div>
          <div v-if="error && failureCodes.length === 0" class="error-message">{{ error }}</div>

        <div class="table-actions">
            <button @click="addNew" :disabled="loading">Add New</button>
            <button @click="startEditing" :disabled="!selectedFailureCode || loading">Edit</button>
            <button @click="deleteFailureCode" :disabled="!selectedFailureCode || loading">Delete</button>
            <button @click="fetchFailureCodes" :disabled="loading">Refresh</button>
          </div>

        <table class="site-table"> <thead>
            <tr>
              <th>Failure Code</th>
              <th>Failure Description</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in failureCodes" :key="item.failure_code"
                :class="{ 'selected': selectedFailureCode && selectedFailureCode.failure_code === item.failure_code }"
                @click="selectFailureCode(item)">
                 <td>{{ item.failure_code }}</td>
               <td>{{ item.failure_desc }}</td>
              </tr>
               <tr v-if="failureCodes.length === 0 && !loading">
                  <td colspan="2" style="text-align: center;">No failure codes found.</td>
               </tr>
          </tbody>
        </table>
      </div>
    </div>

      <div v-if="notification.show" :class="['notification', notification.type, { 'fade-out': !notification.show }]">
      {{ notification.message }}.
      </div>

  </div>
</template>

<style scoped>
/* Styles based on previous views for consistency and professional look */
.failure-code-page { /* Unique class name for this page */
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background-color: #f4f6f8;
    min-height: calc(100vh - 40px); /* Ensure page takes at least full height */
    color: #333; /* Default text color */
}

.container {
    display: flex;
    gap: 20px;
    max-width: 1200px;
    margin: 0 auto;
    flex-wrap: wrap;
    align-items: flex-start; /* Align items to the top */
}

.form-panel, .table-panel {
    background-color: white;
    padding: 25px;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    min-width: 300px;
     box-sizing: border-box; /* Include padding and border in the element's total width and height */
}

.form-panel {
     flex: 1 1 350px; /* Flex-grow, Flex-shrink, Basis */
     max-width: 400px; /* Optional: Limit max width for form */
}

.table-panel {
     flex: 2 1 500px; /* Flex-grow, Flex-shrink, Basis */
    overflow-x: auto;
}


h2 {
    color: #333;
    margin-top: 0;
    margin-bottom: 25px;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
    font-size: 1.5rem;
    font-weight: 600; /* Slightly bolder heading */
}

.form-group {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-group label {
    display: block;
    color: #555;
    font-weight: bold;
    font-size: 0.95rem;
}

input[type="text"],
input[type="number"],
select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
    font-size: 1rem;
    transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
select:disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
    opacity: 0.8;
}

.form-actions {
    margin-top: 30px;
    display: flex;
    gap: 10px;
     flex-wrap: wrap; /* Allow buttons to wrap on small screens */
}

.save-button,
.cancel-button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     min-width: 90px; /* Ensure minimum button width */
     text-align: center;
     flex-grow: 1; /* Allow buttons to grow if needed */
}

.save-button {
    background-color: #28a745; /* Green */
    color: white;
}

.save-button:hover:not(:disabled) {
    background-color: #218838;
}

.cancel-button {
    background-color: #6c757d; /* Gray */
    color: white;
}

.cancel-button:hover:not(:disabled) {
    background-color: #5a6268;
}

button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
}


.table-actions {
    margin-bottom: 15px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.table-actions button {
    padding: 8px 12px;
    border: 1px solid #007bff;
    border-radius: 4px;
    background-color: #007bff;
    color: white;
    cursor: pointer;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     flex-grow: 1; /* Allow buttons to grow */
     min-width: 60px; /* Minimum width for action buttons */
     text-align: center;
}

/* Specific button styles */
.table-actions button:nth-child(2) { /* Edit button */
    background-color: #ffc107;
    border-color: #ffc107;
    color: #212529;
}
.table-actions button:nth-child(2):hover:not(:disabled) {
    background-color: #e0a800;
}

.table-actions button:nth-child(3) { /* Delete button */
    background-color: #dc3545;
    border-color: #dc3545;
    color: white;
}
.table-actions button:nth-child(3):hover:not(:disabled) {
    background-color: #c82333;
}

.table-actions button:hover:not(:disabled) {
    background-color: #0056b3;
    border-color: #0056b3;
}


.site-table { /* Reused class name for styling consistency */
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-size: 0.95rem; /* Slightly larger table font */
    border: 1px solid #ddd;
}

.site-table th,
.site-table td {
    padding: 12px;
    border: 1px solid #ddd;
    text-align: left;
    word-break: break-word; /* Prevent long text from overflowing */
}

.site-table th {
    background-color: #e9ecef;
    font-weight: bold;
    color: #495057;
    text-transform: uppercase;
    font-size: 0.85rem;
}

 .site-table tbody tr:nth-child(even) {
     background-color: #f8f9fa; /* Stripe rows */
 }

.site-table tbody tr:hover {
    background-color: #e9ecef;
    cursor: pointer;
}

.site-table tbody tr.selected {
    background-color: #cfe2ff;
     outline: 2px solid #007bff; /* Use outline instead of border to avoid layout shifts */
     outline-offset: -2px; /* Pull outline inwards */
}

 /* Center text in empty cell */
 .site-table td[colspan="2"] {
     text-align: center;
     font-style: italic;
     color: #666;
 }


/* Responsive adjustments */
@media (max-width: 768px) {
    .container {
        flex-direction: column;
        gap: 15px;
    }

    .form-panel,
    .table-panel {
        margin-bottom: 15px;
        min-width: unset;
        flex: unset;
        width: 100%;
        padding: 15px;
    }

    h2 {
        margin-bottom: 15px;
        padding-bottom: 10px;
        font-size: 1.3rem;
    }

    .form-group {
        margin-bottom: 15px;
    }

    .form-actions {
        margin-top: 20px;
        flex-direction: column;
        gap: 8px;
    }

    .save-button, .cancel-button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }

    .table-actions {
        flex-direction: column;
        gap: 8px;
    }

    .table-actions button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }
}

/* Notification Styling */
.notification {
    position: fixed;
    bottom: 20px;
    left: 20px;
    padding: 15px 20px;
    border-radius: 4px;
    color: white;
    z-index: 1000;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
     opacity: 1; /* Default opacity */
     transition: opacity 0.5s ease-in-out; /* Ensure transition is here */
}

 .notification.fade-out {
     opacity: 0;
 }


.notification.success {
    background-color: #28a745; /* Green */
}

.notification.error {
    background-color: #dc3545; /* Red */
}

.notification.warning {
    background-color: #ffc107; /* Yellow */
    color: #212529;
}

.error-message {
    color: #dc3545;
    margin-bottom: 15px;
    padding: 10px;
    border: 1px solid #dc3545;
    background-color: #fff3f3;
    border-radius: 4px;
     font-size: 0.9rem;
}

</style>
